/**
 * Share a dashboard or a report by link.
 *  - Off (default): the link opens inside Beatly — the person must be signed in
 *    and have permission (visibility) to see it.
 *  - On: "anyone with the link can view" — a public, read-only page, no login
 *    (Looker Studio style). Owner only; the owner can reset the link.
 */
import { useEffect, useRef, useState } from 'react';
import { Link2, Copy, Check, RefreshCw, ExternalLink, Loader2, Globe, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';

export type ShareKind = 'dashboard' | 'report';

export function ShareLinkControl({ kind, id, shareEnabled, shareToken, canEdit = true, onChanged, className }: {
  kind: ShareKind; id: string; shareEnabled: boolean; shareToken: string | null | undefined; canEdit?: boolean;
  onChanged?: (v: { shareEnabled: boolean; shareToken: string }) => void; className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const api = kind === 'dashboard' ? coworkApi.dashboards : coworkApi.presentations;
  const noun = kind === 'dashboard' ? 'dashboard' : 'report';
  const appUrl = kind === 'dashboard' ? `${window.location.origin}/agents/dashboards?open=${id}` : `${window.location.origin}/agents/reports?edit=${id}`;
  const publicUrl = shareEnabled && shareToken ? api.publicUrl(id, shareToken) : '';
  const url = publicUrl || appUrl;
  const set = async (enabled: boolean, rotate = false) => {
    setBusy(true);
    try {
      const r = await api.share(id, { enabled, rotate });
      onChanged?.({ shareEnabled: r.shareEnabled, shareToken: r.shareToken });
      toast.success(rotate ? 'New link created: the old one no longer works' : enabled ? `Anyone with the link can view this ${noun}` : `Link sharing is off: the link now needs sign-in and permission`);
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Could not copy: select the link and copy it'); }
  };
  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} title="Share a link" className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-medium ${shareEnabled ? 'border-[#d6bbfb] bg-[#f9f5ff] text-[#6941c6] hover:bg-[#f4ebff]' : 'border-[#d0d5dd] text-[#344054] hover:bg-[#f9fafb]'}`}>
        <Link2 className="h-3.5 w-3.5" /> {shareEnabled ? 'Public link' : 'Share'}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1.5 w-[22rem] rounded-xl border border-[#eaecf0] bg-white p-3 shadow-xl">
          <div className="text-[12.5px] font-semibold text-[#101828]">Share this {noun}</div>
          <div className="mt-2 flex items-center gap-1.5">
            <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 rounded-md border border-[#d0d5dd] bg-[#f9fafb] px-2 py-1 text-[11.5px] text-[#344054]" />
            <button type="button" onClick={copy} className="flex items-center gap-1 rounded-md bg-[#be43ff] px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-[#a835e0]">{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy'}</button>
          </div>
          <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[#667085]">
            {shareEnabled ? <Globe className="mt-0.5 h-3 w-3 shrink-0 text-[#7c3aed]" /> : <Lock className="mt-0.5 h-3 w-3 shrink-0" />}
            <span>{shareEnabled ? 'Anyone with this link can view it: no login. Read-only, data as of the last refresh.' : 'Opens inside Beatly: the person must be signed in and have permission to see it.'}</span>
          </div>
          <label className={`mt-3 flex items-start gap-2.5 border-t border-[#eaecf0] pt-3 ${canEdit ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
            <input type="checkbox" checked={shareEnabled} disabled={!canEdit || busy} onChange={(e) => void set(e.target.checked)} className="mt-0.5 accent-[#7c3aed]" />
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] font-semibold text-[#101828]">Anyone with the link can view</span>
              <span className="block text-[11px] text-[#667085]">Like a Looker Studio link. Turn it off any time to make the link need sign-in again.</span>
            </span>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#98a2b3]" />}
          </label>
          {!canEdit && <div className="mt-2 text-[11px] text-[#98a2b3]">Only the person who created this {noun} can change that.</div>}
          {shareEnabled && publicUrl && (
            <div className="mt-2 flex items-center justify-between text-[11.5px]">
              <a href={publicUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#7c3aed] hover:underline"><ExternalLink className="h-3 w-3" /> Open the public view</a>
              {canEdit && <button type="button" onClick={() => void set(true, true)} disabled={busy} className="flex items-center gap-1 text-[#667085] hover:text-[#101828]"><RefreshCw className="h-3 w-3" /> Reset link</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
